"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import AwardsSection from "@/components/AwardsSection";
import WhyChooseUsSection from "@/components/WhyChooseUsSection";
import StatsSection from "@/components/StatsSection";
import HomeGetInTouch from "@/components/HomeGetInTouch";

const services = [
  {
    title: "Clinical Trail",
    description:
      "At Quick India Logistics we ensure reliable, timely delivery of clinical trail supplies to pharmaceutical companies, research institutions, and healthcare providers, with precision and safety for every shipment.",
    image: "/assets/photos/home/clinical-768x542.jpg",
    link: "/services/clinical-trail-supply-chain-solution",
  },
  {
    title: "Door to Door Services",
    description:
      "Quick India Logistics specializes in door to door services, ensuring safe, efficient and timely delivery of medicine, medical equipment and more with express of temperature controlled options across India.",
    image: "/assets/photos/home/Door-to-Door-services-1-768x542.jpg",
  },
  {
    title: "Local Distribution",
    description:
      "Quick India Logistics offers flexible local distribution across metropolitan cities in India, ensuring prompt, efficient delivery of products for healthcare, pharmaceuticals and retail industries with a reliable network and closed body vehicles.",
    image: "/assets/photos/services/local-distribution/truck-1024x513.jpg",
  },
];

const HomePage = () => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const backgroundImages = [
    "/assets/photos/home/truck-fin.jpg",
    "/assets/photos/home/train-fin.jpg",
    "/assets/photos/home/flight.jpg",
    "/assets/photos/home/containers.jpg",
    // "/assets/photos/home/healthcare-delivery-1024x513.jpg",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length,
      );
    }, 10000); // Change image every 10 seconds
    return () => clearInterval(interval);
  }, [backgroundImages.length]);
  return (
    <div className="min-h-screen bg-white">
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative pt-2 min-h-screen overflow-hidden"
      >
        {/* Background Images Slideshow */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5 }}
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${backgroundImages[currentImageIndex]})`,
              }}
            >
              {/* Dark Overlay for better text readability */}
              <div className="absolute inset-0 bg-black/40"></div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-10 relative z-10 h-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-[80vh]">
            {/* Left Side - Transparent Navigation Card */}
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-[#003265] backdrop-blur-md rounded-2xl p-4 border-2 border-blue-400/50 shadow-2xl">
                {/* Navigation Links - In One Line */}
                <motion.div
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="flex flex-wrap gap-2 mb-6 p-2 bg-white/5 rounded-lg"
                >
                  {[
                    { href: "/", label: "Home" },
                    { href: "/services", label: "Services" },
                    {
                      href: "/services/clinical-trail-supply-chain-solution",
                      label: "Clinical Trial",
                    },
                  ].map((link, index) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="flex items-center text-base md:text-sm font-semibold text-white hover:text-blue-300 transition-all duration-300 py-2 px-4 rounded-lg hover:bg-white/10 border-r border-blue-400/30 last:border-r-0"
                    >
                      {link.label}
                      {index < 2 && (
                        <span className="ml-4 text-blue-300/60">|</span>
                      )}
                    </Link>
                  ))}
                </motion.div>

                {/* Main Title */}
                <motion.h1
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className="text-sm md:text-sm lg:text-sm font-bold text-white mb-2 leading-tight"
                >
                  Clinical Trial Supply Management: Delivering Precision and
                  Care
                </motion.h1>

                {/* Description */}
                <motion.p
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                  className="text-base md:text-xs text-blue-100 mb-4 leading-relaxed"
                >
                  Ensuring timely and reliable delivery of clinical trial
                  supplies with unmatched precision and care for your healthcare
                  needs.
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.1 }}
                  className="flex flex-col sm:flex-row gap-3"
                >
                  <Link
                    href="/services"
                    className="border-2 border-white text-white px-6 py-3 rounded-lg hover:bg-white hover:text-blue-600 text-base font-semibold text-center transition-all duration-300 hover:scale-105"
                  >
                    Get In Touch
                  </Link>
                </motion.div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-blue-400 rounded-tl-lg"></div>
              <div className="absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-blue-400 rounded-br-lg"></div>
            </motion.div>
          </div>
        </div>

        {/* Background Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
          {backgroundImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentImageIndex
                  ? "bg-white scale-125"
                  : "bg-white/50 hover:bg-white/80"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-white rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* About Section */}
      <section className="py-10 md:px-20 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <motion.h2
              variants={itemVariants}
              className="text-2xl font-bold text-gray-800 mb-4"
            >
              We, Quick India Logistics
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-lg text-gray-600 max-w-4xl mx-auto"
            >
              caters exclusively in the distribution of healthcare and
              pharmaceutical logistics across the Globe.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={itemVariants}>
              <p className="text-sm text-gray-700 leading-relaxed mb-8">
                As our offices are located across India, we guarantee the timely
                deliveries of pharmaceuticals products, medical equipment and
                other time sensitive products to improve the quality of life
                within India as well as across the Globe. The valuable
                experience and the usage of modern equipment allows us to
                provide fast, safe, cost effective, and temperature-controlled
                logistics services that are crucial for the healthcare sector.
              </p>
              <div className="flex space-x-4">
                <button className="bg-[#285784] border-2 border-blue-400 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
                  <Link href={"/about"}>Read More</Link>
                </button>
                <button className="bg-[#922D27] border-2 border-red-400 text-white px-6 py-3 rounded-lg hover:bg-red-700 hover:text-white">
                  <Link href={"/contact"}>Contact Us</Link>
                </button>
              </div>
            </motion.div>
            <motion.div variants={itemVariants} className="relative ">
              <Image
                src="/assets/photos/about/aboutimg.png"
                alt="About Quick India Logistics"
                width={600}
                height={500}
                className=" object-cover rounded-md"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* <section className="py-10 relative overflow-hidden">
         <div className="absolute inset-0 bg-black bg-opacity-90 backdrop-blur-2xl"></div>

        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-bold text-white mb-2"
          >
            Our Services
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-sm text-gray-300 mb-8 max-w-3xl mx-auto"
          >
            Delivering reliable and customized logistics solutions nationwide,
            specializing in temperature controlled, time critical shipments for
            pharmaceutical and biotech industries.
          </motion.p>

           <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="flex justify-center gap-2 mb-10"
          >
            <button className="bg-[#285784] border border-white text-white px-6 py-3 rounded-sm hover:bg-blue-700 transition-colors duration-300 font-medium">
              <Link href={"/services"}>Learn More</Link>
            </button>
            <button className="bg-[#922D27] border border-white text-white px-6 py-3 rounded-sm hover:bg-gray-600 transition-colors duration-300 font-medium">
              <Link href={"/contact"}>Contact Us</Link>
            </button>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid md:grid-cols-3 gap-3 px-35"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-[#6a6a6b] bg-opacity-10 backdrop-blur-md rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden "
              >
                 <div className="h-48 overflow-hidden">
                  <Image
                    width={200}
                    height={200}
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                </div>

                 <div className="p-4">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-200 leading-relaxed mb-4 text-sm">
                    {service.description}
                  </p>

                   <button className="w-1/2 bg-[#922D27] border border-white text-white py-2 px-4 rounded-sm hover:bg-blue-700 transition-colors duration-300 font-medium">
                    Know More
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

 

 
    <section className="py-8 md:py-12 lg:py-16 relative overflow-hidden min-h-screen">
      {/* Dark Blur Background */}
      <div className="absolute inset-0 bg-black bg-opacity-90 backdrop-blur-2xl"></div>
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-red-500 mix-blend-overlay"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        {/* Heading Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-12 lg:mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 sm:mb-4"
          >
            Our <span className="text-blue-400">Services</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="w-20 h-1 bg-gradient-to-r from-blue-500 to-red-500 mx-auto mb-4 sm:mb-6"
          ></motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-sm sm:text-base lg:text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed"
          >
            Delivering reliable and customized logistics solutions nationwide,
            specializing in temperature controlled, time critical shipments for
            pharmaceutical and biotech industries.
          </motion.p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mb-10 sm:mb-12 lg:mb-16"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-[#285784] border-2 border-blue-400 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-700 transition-all duration-300 font-semibold text-sm sm:text-base shadow-lg hover:shadow-blue-500/25"
          >
            <Link href="/services" className="flex items-center gap-2">
              Learn More
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                →
              </motion.span>
            </Link>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-[#922D27] border-2 border-red-400 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-red-700 transition-all duration-300 font-semibold text-sm sm:text-base shadow-lg hover:shadow-red-500/25"
          >
            <Link href="/contact" className="flex items-center gap-2">
              Contact Us
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                ✉️
              </motion.span>
            </Link>
          </motion.button>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8 lg:gap-10"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ 
                y: -8, 
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
              className="group relative bg-gray-900 bg-opacity-50 backdrop-blur-lg rounded-xl shadow-2xl hover:shadow-blue-500/20 border border-gray-700 hover:border-blue-400 overflow-hidden transition-all duration-500"
            >
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0"></div>
              
              {/* Service Image */}
              <div className="relative h-48 sm:h-56 lg:h-64 overflow-hidden">
                <Image
                  src={service.image}
                  alt={service.title}
                  width={400}
                  height={300}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-10 transition-all duration-500"></div> */}
                
                {/* Index Badge */}
                <div className="absolute top-4 left-4 bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </div>
              </div>

              {/* Service Content */}
              <div className="relative z-10 p-6 sm:p-8">
                <motion.h3
                  whileHover={{ color: "#60a5fa" }}
                  className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-300 line-clamp-2"
                >
                  {service.title}
                </motion.h3>
                
                <p className="text-sm text-gray-300 text-sm sm:text-base leading-relaxed mb-6 line-clamp-3">
                  {service.description}
                </p>

                {/* Know More Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-gradient-to-r from-blue-600 to-red-600 hover:from-blue-700 hover:to-red-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 group/btn"
                >
                  <Link href={`${service.link}`}>
                  Know More
                  <motion.span
                    initial={{ x: 0 }}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                    className="group-hover/btn:translate-x-1 transition-transform"
                    >
                    →
                  </motion.span>
                    </Link>
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Mobile Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1 }}
          className="lg:hidden mt-8"
        >
          <p className="text-xs text-gray-400 animate-pulse">
            💡 Scroll to view all services
          </p>
        </motion.div>
      </div>
    </section>
 

 
      <StatsSection />
 
      <WhyChooseUsSection />
      <AwardsSection />
      <HomeGetInTouch />
    </div>
  );
};

export default HomePage;
